import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';

export interface Company {
  id: number;
  name: string;
  city: string;
  units: number;
  type: string;
  startDate: string;
  status: 'active' | 'inactive';
}

const MOCK_COMPANIES: Company[] = [
  { id: 1, name: 'Nutella Ferrero 2021', city: 'Bogotá', units: 12, type: 'Segmented', startDate: '2021-01-18', status: 'active' },
  { id: 2, name: 'Philip Morris International', city: 'Bogotá', units: 8, type: 'Segmented', startDate: '2021-03-15', status: 'active' },
  { id: 3, name: 'Beiersdorf', city: 'Medellín', units: 5, type: 'Segmented', startDate: '2021-05-20', status: 'active' },
  { id: 4, name: 'Nestlé Colombia', city: 'Cali', units: 20, type: 'Segmented', startDate: '2020-11-10', status: 'active' },
  { id: 5, name: 'Pagbank', city: 'São Paulo', units: 3, type: 'Segmented', startDate: '2022-01-05', status: 'inactive' },
  { id: 6, name: 'Corporate Varsal S.A.', city: 'Buenos Aires', units: 7, type: 'Segmented', startDate: '2021-07-22', status: 'active' },
  { id: 7, name: 'Rochinha Sorvetes', city: 'Río de Janeiro', units: 2, type: 'Segmented', startDate: '2022-03-14', status: 'active' },
  { id: 8, name: 'Deuvel', city: 'Bogotá', units: 4, type: 'Segmented', startDate: '2021-09-30', status: 'inactive' },
  { id: 9, name: 'PepsiCo', city: 'Ciudad de México', units: 15, type: 'Segmented', startDate: '2020-06-01', status: 'active' },
  { id: 10, name: 'Bimbo - Barcel', city: 'Guadalajara', units: 11, type: 'Segmented', startDate: '2021-02-28', status: 'active' }
];

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent implements OnInit, AfterViewInit {
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;

  tableData: DynamicTableData;
  searchTerm = '';

  private readonly PAGE_SIZE = 10;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchCompanies.bind(this),
      this.PAGE_SIZE,
      'name',
      1
    );

    const columns = ['name', 'city', 'units', 'type', 'startDate', 'status', 'actions'];

    const cellsConfig: CellsConfig = {
      name: { type: DynamicTableCellType.text, title: 'Name' },
      city: { type: DynamicTableCellType.text, title: 'City' },
      units: { type: DynamicTableCellType.text, title: 'Units' },
      type: { type: DynamicTableCellType.text, title: 'Type' },
      startDate: { type: DynamicTableCellType.text, title: 'Start date' },
      status: { type: DynamicTableCellType.text, title: 'Status' },
      actions: { type: DynamicTableCellType.templateRef, title: ' ', hideTitle: true }
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No companies found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: [10, 25, 50] },
      columns,
      cellsConfig
    };
  }

  private fetchCompanies(page: number, limit: number): Observable<DynamicTableRow[]> {
    const filtered = this.searchTerm
      ? MOCK_COMPANIES.filter(c =>
          c.name.toLowerCase().includes(this.searchTerm) ||
          c.city.toLowerCase().includes(this.searchTerm)
        )
      : MOCK_COMPANIES;

    const start = (page - 1) * limit;
    const rows: DynamicTableRow[] = filtered.slice(start, start + limit).map(c => ({
      name: { value: c.name },
      city: { value: c.city },
      units: { value: String(c.units) },
      type: { value: c.type },
      startDate: { value: new Date(c.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
      status: { value: c.status === 'active' ? 'Active' : 'Inactive', class: `fh-status--${c.status}` },
      actions: { templateRef: this.actionsTemplate, context: { company: c } }
    }));

    return of(rows).pipe(
      delay(0),
      tap(() => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = filtered.length;
        }
      })
    );
  }
}
